/**
 * Scripted screenshot capture (`npm run shots`).
 *
 * Activated by `?shot=<name>`. It lets the scene run for a settling period — long enough for the
 * terrain to stream in and the exposure to adapt — then reads the canvas back and POSTs the PNG to
 * `/__skysim_shot`, which `tools/shots.mjs` writes to `screenshots/`.
 *
 * ## Why this exists rather than someone pressing a key
 *
 * A screenshot of this engine is not a screenshot of a static document: the terrain arrives over
 * several seconds from a cold cache, the auto-exposure adapts over another second, and the cloud
 * layer is only as converged as the number of frames it has had. Captured by hand, the same view
 * gives a different picture every time, which makes the images useless for the one thing they are
 * actually for — telling whether the sky looks better or worse than it did last milestone.
 *
 * So the settle time is a parameter, the framing is a URL, and the result lands on disk under a
 * fixed name. Re-running it produces a comparable image rather than a similar one.
 *
 * The capture is of the **canvas**, so the HUD, the help footer and the airport pill are all
 * absent — they are DOM on top of it. That is what you want in a screenshot and it means the
 * capture needs no "hide the overlays" step that could be forgotten.
 *
 * `updateFrame(alpha)`: called from the loop's after-frame slot, next to the perf harness, and it
 * does nothing at all until its deadline.
 */

import { PRODUCT_NAME } from '../brand.js';
import { composeShot } from './screenshot.js';

export const SHOT_ENDPOINT = '/__skysim_shot';

/** How long the scene runs before the shutter, when the URL does not say [s]. */
const DEFAULT_SETTLE_S = 25;

/** `?shot=<name>` — capture under this name, or null when this is not a screenshot run. */
export function requestedShotName(search: string = globalThis.location?.search ?? ''): string | null {
  const raw = new URLSearchParams(search).get('shot');
  if (raw === null) return null;
  const name = raw.trim();
  return name.length === 0 ? null : name;
}

/** `?settle=<seconds>` — how long to let the world load before the shutter. */
export function requestedSettleSeconds(search: string = globalThis.location?.search ?? ''): number {
  const raw = new URLSearchParams(search).get('settle');
  if (raw === null) return DEFAULT_SETTLE_S;
  const seconds = Number.parseFloat(raw);
  return Number.isFinite(seconds) && seconds >= 0 ? seconds : DEFAULT_SETTLE_S;
}

/**
 * `?card=1200x630` — resample the shot to a fixed frame and stamp the watermark on it.
 *
 * This is how the Open Graph card is made, and making it *in the simulator* rather than in a
 * separate image pipeline is deliberate: the card is a screenshot of the product, so it should be
 * taken by the product, with the same watermark the share button stamps. A build step that crops
 * and letters a PNG afterwards is a second implementation of the corner mark that would drift from
 * the first, and it would need an image library this project does not otherwise want.
 *
 * Returns null for an ordinary shot, which is captured at the canvas' own size and unmarked —
 * milestone comparison images should have nothing painted over the pixels being compared.
 */
export function requestedCardSize(
  search: string = globalThis.location?.search ?? '',
): { widthPx: number; heightPx: number } | null {
  const raw = new URLSearchParams(search).get('card');
  if (raw === null || raw.trim() === '') return null;
  const match = /^(\d{2,5})x(\d{2,5})$/.exec(raw.trim().toLowerCase());
  if (match === null) return null;
  return { widthPx: Number(match[1]), heightPx: Number(match[2]) };
}

/**
 * `?probe=clouds` — read the cloud layer's numbers off the GPU at the shutter and post them.
 *
 * A URL flag rather than always-on because the probes stall the pipeline and render a diagnostic
 * material over the cloud buffer. Nothing that happens in a shipped frame should do either.
 */
export function requestedProbes(search: string = globalThis.location?.search ?? ''): string | null {
  const raw = new URLSearchParams(search).get('probe');
  return raw === null || raw.trim() === '' ? null : raw.trim().toLowerCase();
}

export interface ScreenshotRunOptions {
  name: string;
  canvas: HTMLCanvasElement;
  settleS: number;
  /** Tiles resident right now, so the capture can wait for a world rather than a clock. */
  residentTiles?: () => number;
  /**
   * Resample to this frame and stamp the corner mark. Null captures the canvas as it is.
   *
   * See {@link requestedCardSize} for why the card is composed here rather than post-processed.
   */
  card?: { widthPx: number; heightPx: number } | null;
  /**
   * Milliseconds from navigation start to the aeroplane being flyable, or 0 if not known yet.
   *
   * Reported alongside the image because the throwaway profile this runs in is the only browser
   * around here guaranteed to have a **cold** cache, which makes it the only honest place to
   * measure cold-load-to-flying. See the note at the call site in `main.ts`.
   */
  bootMs?: () => number;
  /**
   * Numbers to read off the GPU at the shutter and post alongside the image.
   *
   * Exists because of a hard constraint on where this project's renderer can be diagnosed. Three's
   * WebGL backend polls shader-compile completion from a `requestAnimationFrame` callback, so a
   * page in a hidden, occluded or embedded browser never finishes start-up on the WebGL2 path —
   * measured at 0 frames in 12 s. The only browser here that reliably *runs* that backend is the
   * throwaway visible Chrome `npm run shots` launches, and nobody can read its console.
   *
   * So a probe that would otherwise be typed into a devtools prompt goes out through the same
   * channel as the picture. Awaited before the post, and after the canvas readback so it cannot
   * disturb the image — {@link VolumetricClouds.probeInternals} renders over the cloud buffer.
   */
  probes?: () => Promise<Record<string, unknown>>;
  onCaptured?: (name: string) => void;
}

export class ScreenshotRun {
  private readonly options: ScreenshotRunOptions;
  private startedAtMs = -1;
  private finished = false;
  /** Frames in a row where the tile count has not moved — the streamer has gone quiet. */
  private steadyFrames = 0;
  private lastResident = -1;

  constructor(options: ScreenshotRunOptions) {
    this.options = options;
  }

  get done(): boolean {
    return this.finished;
  }

  /**
   * Called once per frame. Fires the shutter when the world has settled, or when the clock runs
   * out, whichever comes first.
   *
   * Both conditions are needed. The clock alone captures a half-loaded world on a slow link; the
   * tile count alone never fires over the ocean, where there is nothing to stream and the count is
   * legitimately zero forever.
   */
  updateFrame(nowMs: number): void {
    if (this.finished) return;
    if (this.startedAtMs < 0) {
      this.startedAtMs = nowMs;
      // Report in immediately. A capture run that goes quiet is otherwise indistinguishable from
      // one that never started, and the browser it runs in is a throwaway profile whose console
      // nobody is watching — the same trap the perf harness fell into.
      report({ phase: 'started', name: this.options.name, settleS: this.options.settleS }, true);
      return;
    }

    const elapsedS = (nowMs - this.startedAtMs) / 1000;
    const resident = this.options.residentTiles?.() ?? 0;
    if (resident === this.lastResident) this.steadyFrames++;
    else this.steadyFrames = 0;
    this.lastResident = resident;

    // A third of the settle time as a floor, so exposure and the cloud layer have converged even
    // when the terrain happened to arrive instantly from a warm cache.
    const settled = this.steadyFrames > 180 && elapsedS > this.options.settleS / 3;
    if (!settled && elapsedS < this.options.settleS) return;

    this.finished = true;
    this.capture(elapsedS, resident);
  }

  private capture(elapsedS: number, resident: number): void {
    let png: string;
    try {
      const card = this.options.card ?? null;
      if (card === null) {
        png = this.options.canvas.toDataURL('image/png');
      } else {
        const composed = composeShot(this.options.canvas, {
          widthPx: card.widthPx,
          heightPx: card.heightPx,
        });
        if (composed === null) throw new Error('no 2D context for the card composite');
        png = composed.toDataURL('image/png');
      }
    } catch (error) {
      // Reported rather than only logged: this is the one failure the collector cannot infer, and
      // the console it would go to belongs to a browser profile that is about to be deleted.
      report({ phase: 'failed', name: this.options.name, error: String(error) }, true);
      return;
    }
    console.log(
      `${PRODUCT_NAME} shot "${this.options.name}": captured after ${elapsedS.toFixed(1)}s ` +
        `with ${resident} tiles resident.`,
    );
    const payload: Record<string, unknown> = {
      name: this.options.name,
      png,
      elapsedS,
      resident,
      bootMs: this.options.bootMs?.() ?? 0,
    };

    const probes = this.options.probes;
    if (probes === undefined) {
      report(payload);
      this.options.onCaptured?.(this.options.name);
      return;
    }

    /*
     * Probe, then post. The image is already encoded above, so a probe that stalls the pipeline or
     * scribbles over the cloud buffer cannot affect it.
     *
     * A failing probe still posts the picture. The whole point of this channel is that it is the
     * only way to get a number out of this browser, and losing the shot because the diagnostic
     * threw would be the tool defeating itself.
     */
    void probes()
      .then((values) => {
        payload['probes'] = values;
      })
      .catch((error: unknown) => {
        payload['probes'] = { error: String(error) };
      })
      .finally(() => {
        report(payload);
        this.options.onCaptured?.(this.options.name);
      });
  }
}

/**
 * Post to the collector.
 *
 * **No `keepalive` on the image**, and that is not a detail: `keepalive` requests are capped at
 * 64 KiB by the Fetch standard, and a 1920×1080 PNG data URL is several megabytes. The request is
 * rejected before it leaves the page, the rejection lands in a `.catch` — in the console of a
 * throwaway browser profile nobody is watching — and the collector sees a run that started and
 * then said nothing, which is exactly what it did. The page is not unloading here, so a plain
 * fetch is both correct and unlimited; the small progress posts keep `keepalive` because they are
 * the ones that have to survive the window being closed.
 */
function report(payload: Record<string, unknown>, keepalive = false): void {
  void fetch(SHOT_ENDPOINT, {
    method: 'POST',
    keepalive,
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload),
  }).catch((error: unknown) => {
    console.warn(`${PRODUCT_NAME}: the screenshot collector rejected the post —`, error);
  });
}
